import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Flex, IconButton, Tooltip } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';
import {
   CheckCircleOutlineIcon,
   CheckIcon,
   DeleteIcon,
   EditIcon,
   NotInterestedIcon,
   RemoveRedEyeIcon,
} from 'shared/icons';
import { Resume } from '../types';
import { useResumeManager } from '../resume-manager.provider';
import { ResumeConfirmDialog } from '../resume-confirm-dialog';
import { ResumeStatusDialog } from '../resume-status-dialog/resume-status-dialog';

import './resume-actions-menu.scss';

type ResumeMenuAction = 'STATUS' | 'VERIFY' | 'DELETE' | 'EMPTY';

const ResumeActionsMenuView = ({ resume }: { resume: Resume }) => {
   const navigate = useNavigate();
   const location = useLocation();
   const resumeManager = useResumeManager();
   const [open, setOpen] = React.useState(false);
   const [action, setAction] = React.useState<ResumeMenuAction>('EMPTY');

   const closeDialog = () => {
      setOpen(false);
      setAction('EMPTY');
   };

   return (
      <Flex className="resume-actions-menu" gap="1" align="center" justify="center">
         {resume.is_active === 1 ? (
            <Tooltip content="Deactivate" side="left">
               <IconButton
                  color="green"
                  className="action-trigger"
                  onClick={() => {
                     setOpen(true);
                     setAction('STATUS');
                  }}
               >
                  <CheckIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         ) : (
            <Tooltip content="Activate" side="left">
               <IconButton
                  color="red"
                  className="action-trigger"
                  onClick={() => {
                     setOpen(true);
                     setAction('STATUS');
                  }}
               >
                  <NotInterestedIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         )}

         <Tooltip content="Edit" side="top">
            <IconButton
               color="blue"
               className="action-trigger"
               highContrast
               onClick={() => {
                  navigate(`${resume.id}/edit${location.search}`);
               }}
            >
               <EditIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Delete" side="top">
            <IconButton
               color="red"
               className="action-trigger"
               onClick={() => {
                  setOpen(true);
                  setAction('DELETE');
               }}
            >
               <DeleteIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         {resume.resume_verified !== 1 && (
            <Tooltip content="Verify Now" side="top">
               <IconButton
                  color="green"
                  className="action-trigger"
                  onClick={() => {
                     setOpen(true);
                     setAction('VERIFY');
                  }}
               >
                  <CheckCircleOutlineIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         )}

         <Tooltip content="View" side="top">
            <IconButton
               color="blue"
               className="action-trigger"
               highContrast
               onClick={() => {
                  navigate(`/admin/resumes/${resume.id}${location.search}`);
               }}
            >
               <RemoveRedEyeIcon width={20} height={20} />
            </IconButton>
         </Tooltip>

         <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
               if (!nextOpen) {
                  setAction('EMPTY');
               }
               setOpen(nextOpen);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(event) => event.preventDefault()}
            >
               {
                  {
                     STATUS: (
                        <ResumeStatusDialog
                           loading={
                              resumeManager.activateResumeState.status === 'pending' ||
                              resumeManager.deactivateResumeState.status === 'pending'
                           }
                           onActivate={() => {
                              resumeManager.activateResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    closeDialog();
                                    toast.success(resp.message || 'Resume Activated successfully!');
                                 },
                                 onError: (message) => {
                                    toast.error(message || 'Could not Activate Resume');
                                 },
                              });
                           }}
                           onDeactivate={() => {
                              resumeManager.deactivateResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    closeDialog();
                                    toast.success(
                                       resp.message || 'Resume Deactivated successfully!'
                                    );
                                 },
                                 onError: (message) => {
                                    toast.error(message || 'Could not Deactivate Resume');
                                 },
                              });
                           }}
                        />
                     ),
                     VERIFY: (
                        <ResumeConfirmDialog
                           title="Verify Resume"
                           message="Do you want to Verify this Resume?"
                           loading={resumeManager.verifyResumeState.status === 'pending'}
                           onConfirm={() => {
                              resumeManager.verifyResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    closeDialog();
                                    toast.success(resp.message || 'Resume verified successfully!');
                                 },
                                 onError: (message) => {
                                    toast.error(message || 'Could not Verify Resume');
                                 },
                              });
                           }}
                        />
                     ),
                     DELETE: (
                        <ResumeConfirmDialog
                           title="Delete Resume"
                           message="Do you want to Delete this Resume?"
                           loading={resumeManager.deleteResumeState.status === 'pending'}
                           onConfirm={() => {
                              resumeManager.deleteResume({
                                 id: resume.id,
                                 onSuccess: (resp) => {
                                    closeDialog();
                                    toast.success(resp.message || 'Resume deleted successfully!');
                                 },
                                 onError: (message) => {
                                    toast.error(message || 'Could not Delete Resume');
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const ResumeActionsMenu = observer(ResumeActionsMenuView);
